import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { Chord, ChordQuality, ChordExtensions } from '@/types/chord';
import styles from './ChordEditPopover.module.css';

export interface ChordEditPopoverProps {
  isOpen: boolean;
  position: { x: number; y: number };
  chord: Chord;
  onUpdateChord: (updates: Partial<Chord>) => void;
  onPlayChord?: () => void;
  onClose: () => void;
}

const QUALITY_OPTIONS: { value: ChordQuality; label: string; shortLabel: string }[] = [
  { value: 'major', label: 'Major', shortLabel: 'M' },
  { value: 'minor', label: 'Minor', shortLabel: 'm' },
  { value: 'diminished', label: 'Dim', shortLabel: '°' },
  { value: 'augmented', label: 'Aug', shortLabel: '+' },
];

const SEVENTH_OPTIONS: { value: ChordQuality; label: string; shortLabel: string }[] = [
  { value: 'dom7', label: 'Dom 7', shortLabel: '7' },
  { value: 'maj7', label: 'Maj 7', shortLabel: 'M7' },
  { value: 'min7', label: 'Min 7', shortLabel: 'm7' },
  { value: 'halfdim7', label: 'Half-dim', shortLabel: 'ø7' },
  { value: 'dim7', label: 'Dim 7', shortLabel: '°7' },
];

const EXTENSION_OPTIONS: { key: keyof ChordExtensions; label: string }[] = [
  { key: 'sus2', label: 'sus2' },
  { key: 'sus4', label: 'sus4' },
  { key: 'add9', label: 'add9' },
  { key: 'add11', label: 'add11' },
  { key: 'add13', label: 'add13' },
  { key: 'flat9', label: '♭9' },
  { key: 'sharp9', label: '♯9' },
  { key: 'sharp11', label: '♯11' },
  { key: 'flat13', label: '♭13' },
];

export const ChordEditPopover: React.FC<ChordEditPopoverProps> = ({
  isOpen,
  position,
  chord,
  onUpdateChord,
  onPlayChord,
  onClose,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Adjust position to stay in viewport
  useEffect(() => {
    if (!isOpen || !popoverRef.current) return;

    const popoverRect = popoverRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = position.x;
    let y = position.y;

    // Adjust horizontal
    if (x + popoverRect.width > viewportWidth - 20) {
      x = viewportWidth - popoverRect.width - 20;
    }

    // Adjust vertical - prefer showing above the click if near bottom
    if (y + popoverRect.height > viewportHeight - 20) {
      y = position.y - popoverRect.height - 10;
    }

    // Ensure not off-screen on left/top
    x = Math.max(20, x);
    y = Math.max(20, y);

    setAdjustedPosition({ x, y });
  }, [isOpen, position]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Delay to prevent immediate close from the same click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleQualityChange = useCallback((quality: ChordQuality) => {
    onUpdateChord({ quality });
  }, [onUpdateChord]);

  const handleExtensionToggle = useCallback((extension: keyof ChordExtensions) => {
    const currentExtensions = chord.extensions || {};
    const newExtensions = {
      ...currentExtensions,
      [extension]: !currentExtensions[extension],
    };
    onUpdateChord({ extensions: newExtensions });
  }, [chord.extensions, onUpdateChord]);

  const currentQuality = chord.quality;
  const currentExtensions = chord.extensions || {};

  const content = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={popoverRef}
          className={styles.popover}
          style={{
            left: `${adjustedPosition.x}px`,
            top: `${adjustedPosition.y}px`,
          }}
          initial={{ opacity: 0, scale: 0.95, y: -4 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -4 }}
          transition={{ duration: 0.12 }}
        >
          {/* Quality Section */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Quality</div>
            <div className={styles.pillGroup}>
              {QUALITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`${styles.pill} ${currentQuality === option.value ? styles.pillActive : ''}`}
                  onClick={() => handleQualityChange(option.value)}
                  title={option.label}
                >
                  {option.shortLabel}
                </button>
              ))}
            </div>
          </div>

          {/* Seventh Chords Section */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>7th Chords</div>
            <div className={styles.pillGroup}>
              {SEVENTH_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`${styles.pill} ${currentQuality === option.value ? styles.pillActive : ''}`}
                  onClick={() => handleQualityChange(option.value)}
                  title={option.label}
                >
                  {option.shortLabel}
                </button>
              ))}
            </div>
          </div>

          {/* Extensions Section */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Extensions</div>
            <div className={styles.extensionGroup}>
              {EXTENSION_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  className={`${styles.extensionPill} ${currentExtensions[option.key] ? styles.extensionActive : ''}`}
                  onClick={() => handleExtensionToggle(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Play Button */}
          {onPlayChord && (
            <div className={styles.footer}>
              <button
                className={styles.playButton}
                onClick={() => {
                  onPlayChord();
                }}
                title="Play chord (double-click also works)"
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M4 2.5v11l9-5.5-9-5.5z" />
                </svg>
                Play
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (!isOpen) return null;

  return createPortal(content, document.body);
};
